<template>
  <div class="contents">
    <div class="container-fluid" v-if="invoice">
      <InvoicePageHeader :title="invoice.invoice_number">
        <template #actions>
          <router-link to="/invoice" class="btn px-15 btn-light border mr-2">
            <i class="fas fa-arrow-left"></i> Daftar
          </router-link>
          <router-link v-if="invoice.status === 'draft'" :to="'/invoice/create?id=' + id" class="btn px-15 btn-outline-primary">
            <i class="fas fa-edit"></i> Edit
          </router-link>
        </template>
      </InvoicePageHeader>

      <div class="inv-detail-hero">
        <div>
          <span :class="statusMeta(invoice.status).badge" class="mb-2 d-inline-block">{{ statusMeta(invoice.status).label }}</span>
          <h5 class="mb-1">{{ invoice.customer_name }}</h5>
          <p class="text-muted mb-0 small" v-if="invoice.customer_phone || invoice.customer_email">
            <span v-if="invoice.customer_phone"><i class="fab fa-whatsapp text-success"></i> {{ invoice.customer_phone }}</span>
            <span v-if="invoice.customer_email" class="ml-3"><i class="fas fa-envelope"></i> {{ invoice.customer_email }}</span>
          </p>
        </div>
        <div class="text-lg-right">
          <div class="text-muted small">Total tagihan</div>
          <div class="h3 mb-0 text-primary font-weight-bold">{{ formatRp(invoice.total) }}</div>
        </div>
      </div>

      <div v-if="publicUrl" class="inv-link-box">
        <i class="fas fa-link text-primary"></i>
        <a :href="publicUrl" target="_blank" rel="noopener">{{ publicUrl }}</a>
        <button type="button" class="btn btn-sm btn-primary" @click="copyLink">Salin link</button>
      </div>

      <div class="inv-detail-actions mb-4">
        <button type="button" class="btn btn-primary" :disabled="sending" @click="send">
          <i class="fas fa-paper-plane"></i> {{ sending ? 'Mengirim…' : 'Kirim WA + Email (PDF)' }}
        </button>
        <button v-if="invoice.status !== 'paid' && invoice.status !== 'cancelled'" type="button" class="btn btn-success" @click="markPaid">
          <i class="fas fa-check-circle"></i> Tandai lunas
        </button>
      </div>

      <div class="row">
        <div class="col-lg-8">
          <div class="inv-panel">
            <div class="inv-panel__head"><i class="fas fa-receipt"></i> Rincian item</div>
            <div class="inv-panel__body p-0">
              <div class="inv-table-wrap">
                <table class="table inv-table mb-0">
                  <thead>
                    <tr>
                      <th>Deskripsi</th>
                      <th class="text-center">Qty</th>
                      <th class="text-right">Harga</th>
                      <th class="text-right">Jumlah</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="it in invoice.items" :key="it.id">
                      <td>{{ it.description }}</td>
                      <td class="text-center">{{ it.qty }}</td>
                      <td class="text-right">{{ formatRp(it.unit_price) }}</td>
                      <td class="text-right"><strong>{{ formatRp(it.amount != null ? it.amount : it.qty * it.unit_price) }}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div v-if="invoice.notes" class="inv-panel">
            <div class="inv-panel__head"><i class="fas fa-sticky-note"></i> Catatan</div>
            <div class="inv-panel__body"><p class="mb-0 text-muted">{{ invoice.notes }}</p></div>
          </div>
        </div>
        <div class="col-lg-4">
          <div class="inv-panel">
            <div class="inv-panel__head"><i class="fas fa-calculator"></i> Ringkasan</div>
            <div class="inv-panel__body">
              <div class="d-flex justify-content-between mb-2"><span class="text-muted">Metode bayar</span><span class="text-capitalize">{{ invoice.payment_method || 'manual' }}</span></div>
              <div class="d-flex justify-content-between mb-2" v-if="invoice.discount"><span class="text-muted">Diskon</span><span>{{ formatRp(invoice.discount) }}</span></div>
              <div class="d-flex justify-content-between mb-3" v-if="invoice.tax_percent"><span class="text-muted">Pajak</span><span>{{ invoice.tax_percent }}%</span></div>
              <hr>
              <div class="d-flex justify-content-between align-items-center">
                <span class="font-weight-bold">Total</span>
                <span class="h5 mb-0 text-primary">{{ formatRp(invoice.total) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="container-fluid text-center py-5">
      <div class="spinner-border text-primary"></div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import InvoicePageHeader from '@/components/invoice/InvoicePageHeader.vue';
import { formatRp, statusMeta } from '@/utils/invoiceUi';
import '@/assets/invoice-module.css';

export default {
  components: { InvoicePageHeader },
  props: { id: { type: String, required: true } },
  data() {
    return { invoice: null, publicUrl: '', sending: false };
  },
  mounted() {
    this.load();
  },
  methods: {
    formatRp,
    statusMeta,
    load() {
      axios.get('invoice/invoices/' + this.id).then(r => {
        this.invoice = r.data.data.invoice;
        this.publicUrl = r.data.data.public_url || '';
      }).catch(e => this.$toast.error(e.response?.data?.message || 'Gagal memuat'));
    },
    copyLink() {
      navigator.clipboard.writeText(this.publicUrl);
      this.$toast.success('Link disalin');
    },
    send() {
      this.sending = true;
      axios.post('invoice/invoices/' + this.id + '/send', { channels: ['wa', 'email'] })
        .then(() => { this.$toast.success('Invoice dikirim'); this.load(); })
        .catch(e => this.$toast.error(e.response?.data?.message || 'Gagal kirim'))
        .finally(() => { this.sending = false; });
    },
    markPaid() {
      axios.post('invoice/invoices/' + this.id + '/mark-paid')
        .then(() => { this.$toast.success('Status: lunas'); this.load(); })
        .catch(e => this.$toast.error(e.response?.data?.message || 'Gagal'));
    }
  }
};
</script>
